/**
 * Product Recommendations Slider for Cart Drawer
 * Handles both desktop and mobile product recommendation sliders
 */

class RecommendationsSlider {
  constructor() {
    this.track = null;
    this.slider = null;
    this.isInitialized = false;
    this.products = [];
    
    // Touch/drag properties
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.startTransform = 0;
    this.dragThreshold = 50;
    
    this.bindEvents();
  }
  
  bindEvents() {
    // Listen for cart drawer events
    document.addEventListener('cart-drawer:opened', () => this.initialize());
    document.addEventListener('cart-drawer:updated', () => this.reinitialize());
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.reinitialize(), 500);
    });
  }
  
  initialize() {
    // Wait for DOM to be ready
    this.waitForElement('#recommendationsTrack', (track) => {
      this.track = track;
      this.slider = document.querySelector('.recommendations-slider');
      
      if (this.track && this.slider) {
        this.loadRecommendations();
      }
    });
  }
  
  waitForElement(selector, callback, maxAttempts = 10, interval = 100) {
    let attempts = 0;
    
    const checkElement = () => {
      const element = document.querySelector(selector);
      
      if (element) {
        callback(element);
        return;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkElement, interval);
      } else {
        console.warn(`Element ${selector} not found after ${maxAttempts} attempts`);
      }
    };
    
    checkElement();
  }
  
  loadRecommendations() {
    const productRecommendationsSection = document.querySelector('.drawer__column--right .product-recommendations');
    
    if (!productRecommendationsSection) {
      console.warn('Product recommendations section not found');
      return;
    }
    
    const url = productRecommendationsSection.dataset.url;
    
    if (!url) {
      console.warn('Recommendations URL not found');
      return;
    }
    
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        
        // Look for the specific ul that was in the original template
        const productList = html.querySelector('ul#originalRecommendations');
        
        if (productList && productList.children.length > 0) {
          this.products = Array.from(productList.children).map(li => {
            return { html: li.innerHTML };
          });
          
          this.renderSlider();
          this.isInitialized = true;
        } else {
          // Try alternative: look for any card-wrapper in the response
          const cards = html.querySelectorAll('.card-wrapper, .product-card-wrapper');
          
          if (cards.length > 0) {
            this.products = Array.from(cards).map(card => {
              return { html: card.outerHTML };
            });
            this.renderSlider();
            this.isInitialized = true;
          } else {
            this.showNoRecommendations();
          }
        }
      })
      .catch(error => {
        console.error('Error loading recommendations:', error);
        this.showNoRecommendations();
      });
  }
  
  showNoRecommendations() {
    if (this.track) {
      this.track.innerHTML = '<div style="text-align: center; padding: 20px; color: #666;">No recommendations available</div>';
    }
  }
  
  renderSlider() {
    if (!this.track) return;
    
    if (this.products.length === 0) {
      this.showNoRecommendations();
      return;
    }

    this.track.innerHTML = '';

    this.products.forEach(product => {
      const slide = document.createElement('div');
      slide.className = 'recommendations-slide';
      slide.innerHTML = product.html;
      this.track.appendChild(slide);
    });

    // Initialize card variant pickers for newly added products
    this.initializeCardVariantPickers();
  }
  
  initializeCardVariantPickers() {
    // Wait a bit for DOM to settle, then initialize variant pickers
    setTimeout(() => {
      // Don't call initializeAllCards() here - it's already been called globally
      // and will reset all variant IDs to their defaults
      
      // Only trigger change events on DESKTOP recommendation cards (not mobile)
      const desktopRecommendationCards = document.querySelectorAll('.drawer__column--right .recommendations-slide .card-wrapper');
      
      desktopRecommendationCards.forEach(card => {
        const checkedRadios = card.querySelectorAll('.card-variant-radio:checked');
        
        checkedRadios.forEach(radio => {
          // Trigger a change event to ensure the variant is properly set
          const event = new Event('change', { bubbles: true });
          radio.dispatchEvent(event);
        });
      });
    }, 200); // Increased timeout to ensure DOM is fully ready
  }
  
  reinitialize() {
    this.isInitialized = false;
    this.products = [];
    this.isDragging = false;
    
    // Use setTimeout to ensure DOM has updated
    setTimeout(() => {
      this.initialize();
    }, 150);
  }
}

class MobileRecommendationsSlider {
  constructor() {
    this.track = null;
    this.products = [];
    
    this.bindEvents();
  }
  
  bindEvents() {
    // Listen for cart drawer events
    document.addEventListener('cart-drawer:opened', () => this.initialize());
    document.addEventListener('cart-drawer:updated', () => this.reinitialize());
    document.addEventListener('cart:refresh', () => {
      setTimeout(() => this.reinitialize(), 500);
    });
  }
  
  initialize() {
    this.waitForElement('.recommendations-track--mobile', (track) => {
      this.track = track;
      this.loadRecommendations();
    });
  }
  
  waitForElement(selector, callback, maxAttempts = 10, interval = 100) {
    let attempts = 0;
    
    const checkElement = () => {
      const element = document.querySelector(selector);
      
      if (element) {
        callback(element);
        return;
      }
      
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkElement, interval);
      }
    };
    
    checkElement();
  }
  
  loadRecommendations() {
    const productRecommendationsSection = document.querySelector('.drawer__column--right .product-recommendations');
    
    if (!productRecommendationsSection) {
      return;
    }
    
    const url = productRecommendationsSection.dataset.url;
    
    if (!url) {
      return;
    }
    
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        
        // Look for the specific ul that was in the original template
        const productList = html.querySelector('ul#originalRecommendations');
        
        if (productList && productList.children.length > 0) {
          this.products = Array.from(productList.children).map(li => {
            return { html: li.innerHTML };
          });
          
          this.renderSlider();
        } else {
          // Try alternative: look for any card-wrapper in the response
          const cards = html.querySelectorAll('.card-wrapper, .product-card-wrapper');
          
          if (cards.length > 0) {
            this.products = Array.from(cards).map(card => {
              return { html: card.outerHTML };
            });
            this.renderSlider();
          }
        }
      })
      .catch(error => {
        console.error('Error loading mobile recommendations:', error);
      });
  }
  
  renderSlider() {
    if (!this.track || this.products.length === 0) return;
    
    this.track.innerHTML = '';
    
    this.products.forEach(product => {
      const slide = document.createElement('div');
      slide.className = 'recommendations-slide';
      slide.innerHTML = product.html;
      this.track.appendChild(slide);
    });
    
    // Show the mobile container
    const mobileContainer = document.querySelector('cart-drawer-items .product-recommendations--mobile');
    if (mobileContainer) {
      mobileContainer.style.display = 'flex';
    }

    // Initialize card variant pickers for newly added products
    this.initializeCardVariantPickers();
  }
  
  initializeCardVariantPickers() {
    setTimeout(() => {
      // Don't call initializeAllCards() here - it's already been called globally
      
      // Only trigger change events on MOBILE recommendation cards
      const mobileRecommendationCards = document.querySelectorAll('cart-drawer-items .recommendations-slide .card-wrapper');
      
      mobileRecommendationCards.forEach(card => {
        const checkedRadios = card.querySelectorAll('.card-variant-radio:checked');
        checkedRadios.forEach(radio => {
          const event = new Event('change', { bubbles: true });
          radio.dispatchEvent(event);
        });
      });
    }, 200);
  }
  
  reinitialize() {
    this.products = [];
    setTimeout(() => {
      this.initialize();
    }, 150);
  }
}

// Initialize sliders when DOM is ready
function initializeRecommendationsSliders() {
  if (!window.recommendationsSlider) {
    window.recommendationsSlider = new RecommendationsSlider();
  }
  
  if (!window.mobileRecommendationsSlider) {
    window.mobileRecommendationsSlider = new MobileRecommendationsSlider();
  }
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRecommendationsSliders);
} else {
  initializeRecommendationsSliders();
}

// Export for manual initialization if needed
window.initializeRecommendationsSliders = initializeRecommendationsSliders;